// ═══════════════════════════════════════════════════════════════════════════
// LA POINTEUSE DÈS L'OUVERTURE — où elle mène, et pour qui (25/10/2026)
// ───────────────────────────────────────────────────────────────────────────
// Fondateur, 25/10/2026 : « après les 5 s d'intro, 3 s sur la pointeuse », puis
// « ça doit apparaître dans le site dès le début pour qui de droit ». Ce module
// porte les trois décisions, PURES, pour que l'intro (SplashIntro), la vitrine
// et le bandeau de l'app lisent la même chose :
//   · LA VIDÉO : un clip de 3 s rendu à partir d'une animation HTML de la
//     pointeuse (badge QR, arrivée / pause / départ, reste à récupérer),
//     enregistré par Playwright, encodé en VP8/WebM par le ffmpeg qu'il
//     embarque (aucun encodeur H.264 disponible : pas de MP4). Un navigateur
//     qui ne lit pas le WebM (Safari iOS ancien) reçoit l'image fixe `poster`.
//     Deux fichiers, FR et NL : l'app est bilingue (R8), et le clip porte du texte.
//   · QUI DE DROIT : ce n'est pas un rôle écrit en dur, c'est le MENU — un
//     compte voit le bandeau si `canAccess` lui donne l'écran de la pointeuse
//     qui lui correspond (ajouts et retraits par personne compris, cf.
//     pages-ouvertes-client.js). Le rôle ne sert qu'à choisir la DESTINATION.
// PUR — aucune I/O. Verrou : __tests__/pointeuse-accueil.test.js (exécute).
// ═══════════════════════════════════════════════════════════════════════════

import { langueArrivee } from './langue-arrivee';

// 🇳🇱 L'APP EST BILINGUE (fondateur, 25/10/2026) : le clip porte du TEXTE (titre, lignes du
// badge, compteur), donc il existe en deux fichiers, rendus depuis la même animation HTML.
// `videoPointeuse(lang)` choisit ; `VIDEO_POINTEUSE` reste le français (lecteurs existants).
const CLIP = (suffixe) => Object.freeze({
  src: `/videos/pointeuse-3s${suffixe}.webm`,
  type: 'video/webm',
  poster: `/videos/pointeuse-3s${suffixe}.jpg`,
  dureeMs: 3000,
});
/** Le clip de 3 s et son image fixe (public/videos), par langue. `dureeMs` est la durée des fichiers, lue à l'encodage (00:00:03.00). */
export const VIDEOS_POINTEUSE = Object.freeze({ fr: CLIP(''), nl: CLIP('-nl') });
export const VIDEO_POINTEUSE = VIDEOS_POINTEUSE.fr;

/** Le clip dans la langue demandée ; toute autre langue (en, de, inconnue) retombe sur le français. */
export function videoPointeuse(lang) {
  return VIDEOS_POINTEUSE[String(lang || '').toLowerCase()] || VIDEOS_POINTEUSE.fr;
}

/** Les mots de l'intro, par langue : « Passer » sous l'animation, la mention sous le clip, le texte alternatif. */
export const LIBELLES_INTRO = Object.freeze({
  fr: Object.freeze({ passer: 'Passer', clip: 'Pointeuse · Passer', alt: 'Pointeuse Aureus Social Pro — badge QR, Mes heures, heures supplémentaires récupérées' }),
  nl: Object.freeze({ passer: 'Overslaan', clip: 'Prikklok · Overslaan', alt: 'Prikklok Aureus Social Pro — QR-badge, Mijn uren, gerecupereerde overuren' }),
});

/**
 * La langue de l'intro = la langue de la PAGE qu'elle recouvre, jamais celle du navigateur :
 * un clip néerlandais au-dessus d'une page française serait incohérent.
 *   1. `document.documentElement.lang` — posé par LangDocument sur toute page rédigée en
 *      néerlandais (/nl, /gids, les guides NL, les landings NL) avant la peinture ;
 *   2. la racine `/` est la page d'accueil FRANÇAISE (la néerlandaise est `/nl`) ;
 *   3. ailleurs (/pricing, /legal, /login…), la règle de ces pages : `?lang=`, puis le
 *      dernier choix retenu (langueArrivee), puis le français.
 * @param {{langDocument?:string, pathname?:string, recherche?:string, memorisee?:string|null}} p
 * @returns {'fr'|'nl'}
 */
export function langueIntro({ langDocument = '', pathname = '/', recherche = '', memorisee = null } = {}) {
  if (/^nl\b/i.test(String(langDocument || '').trim())) return 'nl';
  if (String(pathname || '/') === '/') return 'fr';
  return langueArrivee({ recherche, memorisee, disponibles: ['fr', 'nl'], defaut: 'fr' });
}

/** Clé sessionStorage du bandeau de l'app : fermé une fois = fermé pour la session (jamais localStorage : il reviendrait à chaque connexion… ou jamais). */
export const BANDEAU_POINTEUSE_KEY = 'aureus_bandeau_pointeuse_v1';

/**
 * Où la pointeuse s'ouvre pour ce compte — ou `null` s'il n'y a pas droit.
 * @param {string} role       rôle affiché (roleAffiche)
 * @param {(id:string)=>boolean} canAccess  `canAccessPage(role, id, overrides)` du layout — la seule autorité
 * @returns {{page:string, tab:string|null, cle:string}|null}  `cle` nomme le libellé à traduire (tx)
 */
export function cibleAccueilPointeuse(role, canAccess) {
  const peut = (id) => { try { return canAccess(id) === true; } catch (_) { return false; } };
  const r = String(role || '').toLowerCase();
  // Le travailleur : ses propres heures (onglet « Mes heures » du portail).
  if (r === 'employe') return peut('portailemploye') ? { page: 'portailemploye', tab: 'heures', cle: 'Ouvrir Mes heures' } : null;
  // L'employeur et son RH : l'onglet Pointeuse de Smart Planning Pro (relevés, « Qui est là », corrections).
  if (r === 'employeur' || r === 'rh_entreprise') return peut('smartplanning') ? { page: 'smartplanning', tab: 'pointage', cle: 'Ouvrir la pointeuse' } : null;
  // Le personnel d'Aureus : la pointeuse / badgeuse (kiosque, badge QR), sinon Smart Planning Pro.
  if (peut('pointeusebadgeuse')) return { page: 'pointeusebadgeuse', tab: null, cle: 'Ouvrir la pointeuse' };
  if (peut('smartplanning')) return { page: 'smartplanning', tab: 'pointage', cle: 'Ouvrir la pointeuse' };
  return null;
}
